/* One vocabulary for everything an agent does.

   Three surfaces used to disagree about what a run contains: the chat timeline
   (RunTimeline) knew about memory, skills, subagents and handoffs; the trace
   tree (traceModel) knew only llm / tool / message; team receipts (teams/engine)
   spoke a third dialect of `node:` and `handoff:` events. The same run therefore
   read differently depending on where you looked at it.

   This registry is the single source of truth: every kind an agent can perform,
   with the glyph, tone and wording used to render it. Nothing else may invent a
   span kind or pick its own icon.

   TONE IS NOT DECORATION — it is the legend, and it has exactly five values:
     info    (blue)    model inference — the only place tokens are spent
     warn    (amber)   waiting on a human, or stopped by policy
     danger  (red)     failed
     success (green)   verified against a contract
     neutral           everything else
   A sixth colour would make the first five mean less, so there isn't one. */

import {
  ArrowRightLeft, BadgeCheck, Blocks, Book, Box, Braces, CalendarClock, CheckCheck,
  CornerDownRight, Cpu, Database, FileDiff, FileText, Globe, Layers, ListChecks,
  MessageSquare, Plug, RefreshCw, Scissors, ShieldAlert, Sparkles, Split, SquareTerminal,
  UserCheck, Users, Webhook, Wrench,
} from "lucide-react";

import type { LucideIcon } from "lucide-react";

import type { DensityId, DensityLevel, EffectTool, Tone, TraceKind } from "@/shared/types";

/* Tone → the ring/background/text triad already defined in index.css. Callers
   never write these class strings themselves. */
export const TONES: Record<Tone, string> = {
  info: "bg-[var(--info-bg)] ring-[var(--info-ring)] text-[var(--info)]",
  warn: "bg-[var(--warn-bg)] ring-[var(--warn-ring)] text-[var(--warn-text)]",
  danger: "bg-[var(--danger-bg)] ring-[var(--danger-ring)] text-[var(--danger)]",
  success: "bg-[var(--success-bg)] ring-[var(--success-ring)] text-[var(--success)]",
  neutral: "bg-[var(--surface)] ring-[var(--hairline-strong)] text-[var(--text-secondary)]",
};

/* Bar fills for the waterfall. Same five-tone legend, flattened to a single
   colour because a bar is read at a glance, not inspected. */
export const BAR_TONES: Record<Tone, string> = {
  info: "bg-[var(--info-bg)] ring-[var(--info-ring)]",
  warn: "bg-[var(--warn-bg)] ring-[var(--warn-ring)]",
  danger: "bg-[var(--danger-bg)] ring-[var(--danger-ring)]",
  success: "bg-[var(--success-bg)] ring-[var(--success-ring)]",
  neutral: "bg-[var(--surface-2)] ring-[var(--hairline-strong)]",
};

/* density: which levels show this kind.
     1 = Compact   — the shape of the run: what it decided, what it changed
     2 = Balanced  — plus the work: tools, retrieval, subagents
     3 = Detailed  — plus the machinery: thinking, cache, compaction, hooks
   A kind absent from Compact is never *lost* — the row it collapses into
   carries a count, so nothing disappears silently. */
/** How one kind renders, and how deep you have to look to see it. */
interface KindMeta {
  Icon: LucideIcon;
  label: string;
  tone: Tone;
  density: DensityLevel;
  /** Contains other spans, so it survives every density filter. */
  group?: boolean;
  /** Spends tokens. */
  billable?: boolean;
  /** Changes something outside the workspace. */
  effect?: boolean;
}

const K = (Icon: LucideIcon, label: string, tone: Tone, density: DensityLevel, opts: Partial<KindMeta> = {}): KindMeta =>
  ({ Icon, label, tone, density, ...opts });

export const KINDS: Record<TraceKind, KindMeta> = {
  /* structural */
  agent: K(Sparkles, "Run", "neutral", 1, { group: true }),
  turn: K(CornerDownRight, "Turn", "neutral", 1, { group: true }),
  trigger: K(CalendarClock, "Trigger", "neutral", 2),

  /* inference — the only blue */
  llm: K(Cpu, "Generation", "info", 1, { billable: true }),
  thinking: K(Braces, "Thinking", "info", 3, { billable: true }),
  // Jev's pick between an agent's two models: a decision, so even Compact shows it
  route: K(Split, "Router", "info", 1),

  /* work */
  tool: K(Wrench, "Tool", "neutral", 2),
  mcp: K(Plug, "MCP", "neutral", 2),
  code: K(SquareTerminal, "Sandbox", "neutral", 2),
  search: K(Globe, "Web search", "neutral", 2),
  edit: K(FileDiff, "File edit", "neutral", 1, { effect: true }),

  /* context assembly */
  memory: K(Database, "Memory", "neutral", 2),
  knowledge: K(Book, "Knowledge", "neutral", 2),
  skill: K(Blocks, "Skill", "neutral", 2),
  compaction: K(Scissors, "Compaction", "neutral", 3),

  /* intent */
  plan: K(ListChecks, "Plan", "neutral", 1),
  message: K(MessageSquare, "Reply", "neutral", 1),

  /* delegation */
  subagent: K(Users, "Subagent", "neutral", 2, { group: true }),
  handoff: K(ArrowRightLeft, "Handoff", "neutral", 1),
  verifier: K(BadgeCheck, "Verifier", "success", 1),

  /* human + policy */
  approval: K(UserCheck, "Approval", "warn", 1),
  guardrail: K(ShieldAlert, "Guardrail", "warn", 1),
  retry: K(RefreshCw, "Retry", "warn", 2),

  /* outbound side effects */
  webhook: K(Webhook, "Webhook", "neutral", 1, { effect: true }),
};

const FALLBACK = K(Box, "Step", "neutral", 2);

// A span may carry a kind the registry has never heard of — that is what
// FALLBACK is for, so the lookup takes any string.
export const kindOf = (kind: string | null | undefined): KindMeta => KINDS[kind as TraceKind] || FALLBACK;

/* Status always wins over kind: a failed generation is red before it is blue,
   because "did it work" is the first question anyone asks of a trace. */
export function toneFor(kind: string | undefined, status: string | undefined): Tone {
  if (status === "error") return "danger";
  if (status === "pending") return "warn";
  if (status === "skipped") return "neutral";
  return kindOf(kind).tone;
}

/* ------------------------------ density ---------------------------------- */

export const DENSITY: [id: DensityId, label: string, level: DensityLevel, hint: string][] = [
  ["compact", "Compact", 1, "Decisions, edits and replies"],
  ["balanced", "Balanced", 2, "Plus tools, retrieval and subagents"],
  ["detailed", "Detailed", 3, "Everything, including cache and thinking"],
];

export const densityLevel = (id: string | undefined): DensityLevel => DENSITY.find((d) => d[0] === id)?.[2] ?? 2;

/* A span survives the density filter if its own kind is at or below the level.
   Errors and human-blocking steps ignore density entirely — hiding the one
   step that needs attention is the only unforgivable thing a filter can do. */
export function passesDensity(span: { kind?: string; status?: string }, level: number): boolean {
  if (span.status === "error" || span.status === "pending") return true;
  if (kindOf(span.kind).group) return true;
  return kindOf(span.kind).density <= level;
}

/* ------------------------------ side effects ------------------------------ */

/* Tools that change something outside the workspace. The trace is the only
   record the user has of these, so they are surfaced separately from the run
   itself — "what did it touch" is a different question from "what did it do".
   `undo` is null where the action genuinely cannot be taken back. */
/* `held` is the phrasing used while the action is still waiting on a human.
   The past-tense verb cannot be reused there — "Sent email … nothing sent" is
   the kind of sentence that makes people stop trusting the ledger. */
export const EFFECT_TOOLS: Record<string, EffectTool | null> = {
  "gmail.send": { verb: "Sent email", held: "Email waiting to send", undo: null, severity: "high" },
  "gmail.draft": { verb: "Drafted email", held: "Draft waiting", undo: "Delete draft", severity: "low" },
  "slack.post": { verb: "Posted to Slack", held: "Slack post waiting", undo: "Delete message", severity: "high" },
  "notion.create_page": { verb: "Created Notion page", held: "Page waiting to be created", undo: "Archive page", severity: "medium" },
  "airtable.append": { verb: "Appended rows", held: "Rows waiting to be written", undo: "Delete rows", severity: "medium" },
  "airtable.query": null, // read-only
  "drive.write": { verb: "Wrote file", held: "File waiting to be written", undo: "Restore version", severity: "medium" },
  "gcalendar.create": { verb: "Created event", held: "Event waiting to be created", undo: "Delete event", severity: "medium" },
  "artifact.create": { verb: "Created artifact", held: "Artifact waiting", undo: "Delete artifact", severity: "low" },
  "artifact.edit": { verb: "Edited artifact", held: "Edit waiting", undo: "Restore version", severity: "low" },
};

export const effectOf = (tool: string | undefined): EffectTool | null => (tool ? EFFECT_TOOLS[tool] || null : null);

// The right phrasing for an effect given whether it actually happened.
export const effectVerb = (e: EffectTool, status: string | undefined): string =>
  status === "pending" ? e.held || `${e.verb} — held` : e.verb;

/* Read vs write. Anything not known to write is treated as a read — the
   ledger under-claims rather than over-claims, so a row appearing in it is
   always real. */
export const isWrite = (tool: string | undefined): boolean => Boolean(effectOf(tool));
